import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Chip,
    Grid,
    Divider,
    Stack,
    Avatar
} from '@mui/material';
import {
    FlightTakeoff as FlightIcon,
    Schedule as ScheduleIcon,
    Height as HeightIcon,
    Adjust as RadiusIcon,
    Share as ShareIcon,
    Lock as LockIcon,
    CalendarToday as CalendarIcon,
    Place as PlaceIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

// Mapbox access token
mapboxgl.accessToken = 'pk.eyJ1IjoidGhpbGFua2FkYW1pdGgiLCJhIjoiYjBUcG1DWSJ9.qk0Lif3yooEtjwaC5x8cFg';

/**
 * Template Preview Dialog Component
 * Shows detailed information about an operation template with map preview
 *
 * Props:
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close dialog callback
 * @param {Object} template - Template to preview
 * @param {function} onUse - Optional callback when "Use Template" is clicked
 */
const TemplatePreviewDialog = ({ open, onClose, template, onUse }) => {
    const navigate = useNavigate();
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const [mapLoaded, setMapLoaded] = useState(false);

    // Initialize map when dialog opens
    useEffect(() => {
        if (!open || !template) {
            return;
        }

        // Wait for the dialog animation and container to be ready
        const timer = setTimeout(() => {
            if (!mapContainerRef.current) {
                return;
            }

            if (mapRef.current) {
                mapRef.current.resize();
                return;
            }

            try {
                const map = new mapboxgl.Map({
                    container: mapContainerRef.current,
                    style: 'mapbox://styles/mapbox/streets-v12',
                    center: [-95.7129, 37.0902], // Default US center
                    zoom: 4
                });

                map.on('load', () => {
                    setMapLoaded(true);
                    // Force a resize to ensure map displays correctly
                    setTimeout(() => {
                        if (map) {
                            map.resize();
                        }
                    }, 100);
                });

                map.on('error', (e) => {
                });

                mapRef.current = map;
            } catch (error) {
            }
        }, 500); // Increased delay to ensure dialog animation completes

        return () => {
            clearTimeout(timer);
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
                setMapLoaded(false);
            }
        };
    }, [open, template]);

    // Display geometry on map
    useEffect(() => {
        if (!mapLoaded || !mapRef.current || !template?.operationGeometry) return;

        const map = mapRef.current;

        try {
            // Remove existing layers if any
            if (map.getLayer('template-fill')) map.removeLayer('template-fill');
            if (map.getLayer('template-outline')) map.removeLayer('template-outline');
            if (map.getSource('template-geometry')) map.removeSource('template-geometry');

            // Normalize geometry structure
            let geometryToDisplay = template.operationGeometry;

            // If it's a Feature, use it directly
            if (geometryToDisplay.type === 'Feature') {
                geometryToDisplay = geometryToDisplay;
            }
            // If it's a FeatureCollection, use the first feature
            else if (geometryToDisplay.type === 'FeatureCollection') {
                geometryToDisplay = geometryToDisplay.features[0];
            }
            // If it's direct geometry (Polygon), wrap it
            else if (geometryToDisplay.type === 'Polygon' || geometryToDisplay.type === 'LineString') {
                geometryToDisplay = {
                    type: 'Feature',
                    geometry: geometryToDisplay
                };
            }


            // Add source
            map.addSource('template-geometry', {
                type: 'geojson',
                data: geometryToDisplay
            });

            // Add fill layer
            map.addLayer({
                id: 'template-fill',
                type: 'fill',
                source: 'template-geometry',
                paint: {
                    'fill-color': '#088',
                    'fill-opacity': 0.3
                }
            });

            // Add outline layer
            map.addLayer({
                id: 'template-outline',
                type: 'line',
                source: 'template-geometry',
                paint: {
                    'line-color': '#088',
                    'line-width': 3
                }
            });

            // Calculate bounds and fit map
            const coordinates = geometryToDisplay.geometry.coordinates[0];
            const bounds = coordinates.reduce((bounds, coord) => {
                return bounds.extend(coord);
            }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

            map.fitBounds(bounds, {
                padding: 50,
                duration: 1000
            });

        } catch (error) {
        }
    }, [mapLoaded, template]);

    if (!template) {
        return null;
    }

    const handleUseTemplate = () => {
        if (onUse) {
            onUse(template);
        } else {
            // Default behavior: navigate to create operation page
            navigate('/operations/create', {
                state: {
                    templateId: template.id,
                    templateGeometry: template.operationGeometry,
                    templateData: {
                        operationType: template.operationType,
                        maxAltitude: template.maxAltitude,
                        radius: template.radius,
                        duration: template.duration,
                        timezone: template.timezone
                    }
                }
            });
        }
        onClose();
    };

    // Detect geometry type from template
    const detectGeometryType = () => {
        if (!template.operationGeometry) return 'Unknown';

        const geomCoords = template.operationGeometry?.geometry?.coordinates?.[0] ||
                          template.operationGeometry?.features?.[0]?.geometry?.coordinates?.[0] ||
                          template.operationGeometry?.coordinates?.[0];

        if (!geomCoords) return 'Unknown';

        const pointCount = geomCoords.length;
        return pointCount >= 100 ? 'Circle' : 'Custom Polygon';
    };

    const geometryType = detectGeometryType();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" component="span" fontWeight={600}>
                        {template.name}
                    </Typography>
                    {template.isShared ? (
                        <Chip
                            icon={<ShareIcon fontSize="small" />}
                            label="Shared"
                            size="small"
                            color="success"
                            variant="outlined"
                        />
                    ) : (
                        <Chip
                            icon={<LockIcon fontSize="small" />}
                            label="Private"
                            size="small"
                            variant="outlined"
                        />
                    )}
                </Box>
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={3}>
                    {/* Left column - Map */}
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight={600}>
                                Geometry Preview
                            </Typography>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: 400,
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    position: 'relative',
                                    backgroundColor: '#e0e0e0'
                                }}
                            >
                                <Box
                                    ref={mapContainerRef}
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0
                                    }}
                                />
                                {!mapLoaded && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            textAlign: 'center',
                                            zIndex: 1
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            Loading map...
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Grid>

                    {/* Right column - Details */}
                    <Grid item xs={12} md={6}>
                        {/* Description */}
                        {template.description && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body1" color="text.secondary">
                                    {template.description}
                                </Typography>
                            </Box>
                        )}

                        <Divider sx={{ mb: 2 }} />

                        {/* Operation Details */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
                                Operation Details
                            </Typography>

                            <Stack spacing={2}>
                                {/* Operation Type */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FlightIcon color="action" fontSize="small" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Operation Type
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {template.operationType === 'part_107' ? 'Part 107' : '44809'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Duration */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ScheduleIcon color="action" fontSize="small" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Duration
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {template.duration} minutes
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Geometry Type */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PlaceIcon color="action" fontSize="small" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Geofence Type
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {geometryType}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Stack>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {/* Flight Parameters */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
                                Flight Parameters
                            </Typography>

                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {template.maxAltitude && (
                                    <Chip
                                        icon={<HeightIcon fontSize="small" />}
                                        label={`Altitude: ${template.maxAltitude} ft`}
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                                {template.radius && (
                                    <Chip
                                        icon={<RadiusIcon fontSize="small" />}
                                        label={`Radius: ${template.radius} ft`}
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                            </Stack>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {/* Creator Info */}
                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
                                Template Information
                            </Typography>

                            <Stack spacing={2}>
                                {/* Creator */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: 'primary.main' }}>
                                        {template.createdBy?.name?.charAt(0) || 'U'}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Created By
                                        </Typography>
                                        <Typography variant="body2" fontWeight={500}>
                                            {template.createdBy?.name || 'Unknown'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Created Date */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CalendarIcon color="action" fontSize="small" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Created On
                                        </Typography>
                                        <Typography variant="body2" fontWeight={500}>
                                            {new Date(template.createdAt).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Stack>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>
                    Close
                </Button>
                <Button
                    onClick={handleUseTemplate}
                    variant="contained"
                    startIcon={<FlightIcon />}
                    size="large"
                >
                    Use Template
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TemplatePreviewDialog;
